(function () {
  const COLOR_MAP = {
    orange: '#c45c32',
    coral: '#e07a3d',
    navy: '#1e3a5f',
    blue: '#1e3a8a',
    black: '#1a1a1a',
    cream: '#e8d5c4',
    white: '#f4eee8',
    red: '#9f1d2b',
    pink: '#d46a8a',
    green: '#1f6b4a',
    gold: '#c9a227',
    silver: '#b8b8b8',
    purple: '#5b3a7a',
    brown: '#7c4a2d',
    clear: '#d9d0c6',
  };

  const COPY = {
    loadingModel: 'Loading the illustration model… first visit can take a few seconds.',
    converting: 'Turning your photo into a line drawing…',
    ready: 'Line drawing ready. Drag to place it on the case.',
    convertError: 'Could not convert that image. Try a JPG or PNG.',
    unsupported: 'Use a JPG, PNG, or WEBP photo.',
    needPhoto: 'Upload a photo first.',
    needModel: 'Select your phone model.',
    adding: 'ADDING…',
    added: 'ADDED ✓',
    addError: 'Could not add to cart. Try again.',
  };

  const LINEART = {
    ortUrl: 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.21.0/dist/ort.min.js',
    wasmPath: 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.21.0/dist/',
    drawUrl:
      'https://cdn.jsdelivr.net/gh/Kazuhito00/Informative-Drawings-ONNX-Sample@main/model/anime_style_512x512.onnx',
    cutoutUrls: [],
    drawSize: 512,
    cutoutSize: 320,
  };

  let enginesPromise = null;

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (window.ort) {
        resolve(window.ort);
        return;
      }
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve(window.ort), { once: true });
        existing.addEventListener('error', () => reject(new Error(COPY.convertError)), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve(window.ort);
      script.onerror = () => reject(new Error(COPY.convertError));
      document.head.appendChild(script);
    });
  }

  async function createWasmSession(ort, url) {
    return ort.InferenceSession.create(url, { executionProviders: ['wasm'] });
  }

  function ensureEngines() {
    if (enginesPromise) return enginesPromise;

    enginesPromise = loadScript(LINEART.ortUrl).then(async (ort) => {
      if (!ort) throw new Error(COPY.convertError);
      ort.env.wasm.wasmPaths = LINEART.wasmPath;
      ort.env.wasm.numThreads = 1;
      const draw = await createWasmSession(ort, LINEART.drawUrl);
      let cutout = null;
      const cutoutUrls = [];
      const scriptEl = document.querySelector('script[data-pc-cutout]');
      if (scriptEl && scriptEl.dataset.pcCutout) cutoutUrls.push(scriptEl.dataset.pcCutout);
      LINEART.cutoutUrls.forEach((url) => cutoutUrls.push(url));
      for (let i = 0; i < cutoutUrls.length; i += 1) {
        try {
          cutout = await createWasmSession(ort, cutoutUrls[i]);
          break;
        } catch (error) {
          cutout = null;
        }
      }
      return { ort, draw, cutout };
    });

    enginesPromise.catch(() => {
      enginesPromise = null;
    });

    return enginesPromise;
  }

  function formatMoney(cents, currency) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'INR',
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function colorFromName(name) {
    const key = String(name || '').toLowerCase();
    const match = Object.keys(COLOR_MAP).find((token) => key.includes(token));
    return match ? COLOR_MAP[match] : '#c45c32';
  }

  function paperFromBumper(hex) {
    const value = hex.replace('#', '');
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);
    const light = (r * 299 + g * 587 + b * 114) / 1000 > 160;
    return light ? '#f7ebdf' : '#f3e2d2';
  }

  function sourceSize(image) {
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
  }

  function letterboxPhoto(image, size, fill) {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = fill || '#ffffff';
    ctx.fillRect(0, 0, size, size);
    const src = sourceSize(image);
    const scale = Math.min(size / src.width, size / src.height);
    const width = src.width * scale;
    const height = src.height * scale;
    const x = (size - width) / 2;
    const y = (size - height) / 2;
    ctx.drawImage(image, x, y, width, height);
    return { canvas, ctx, x, y, width, height };
  }

  function canvasToPlanarRgb(ctx, size, normalize) {
    const pixels = ctx.getImageData(0, 0, size, size).data;
    const plane = size * size;
    const tensor = new Float32Array(plane * 3);
    const mean = [0.485, 0.456, 0.406];
    const std = [0.229, 0.224, 0.225];
    for (let i = 0, p = 0; i < pixels.length; i += 4, p += 1) {
      let r = pixels[i] / 255;
      let g = pixels[i + 1] / 255;
      let b = pixels[i + 2] / 255;
      if (normalize) {
        r = (r - mean[0]) / std[0];
        g = (g - mean[1]) / std[1];
        b = (b - mean[2]) / std[2];
      } else {
        r *= 255;
        g *= 255;
        b *= 255;
      }
      tensor[p] = r;
      tensor[plane + p] = g;
      tensor[plane * 2 + p] = b;
    }
    return tensor;
  }

  function minMaxNormalize(data) {
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < data.length; i += 1) {
      const value = data[i];
      if (value < min) min = value;
      if (value > max) max = value;
    }
    const span = Math.max(1e-6, max - min);
    const out = new Float32Array(data.length);
    for (let i = 0; i < data.length; i += 1) out[i] = (data[i] - min) / span;
    return out;
  }

  async function subjectMask(image, session, ort) {
    if (!session) return null;
    const size = LINEART.cutoutSize;
    const boxed = letterboxPhoto(image, size, '#ffffff');
    const tensor = canvasToPlanarRgb(boxed.ctx, size, true);
    const feeds = {};
    feeds[session.inputNames[0]] = new ort.Tensor('float32', tensor, [1, 3, size, size]);
    const results = await session.run(feeds);
    const output = results[session.outputNames[0]];
    const mask = minMaxNormalize(output.data);
    const src = sourceSize(image);
    const canvas = document.createElement('canvas');
    canvas.width = src.width;
    canvas.height = src.height;
    const square = document.createElement('canvas');
    square.width = size;
    square.height = size;
    const squareCtx = square.getContext('2d');
    const imageData = squareCtx.createImageData(size, size);
    for (let p = 0, i = 0; p < mask.length; p += 1, i += 4) {
      const keep = mask[p] > 0.52 ? 1 : 0;
      const value = Math.round(keep * 255);
      imageData.data[i] = value;
      imageData.data[i + 1] = value;
      imageData.data[i + 2] = value;
      imageData.data[i + 3] = 255;
    }
    squareCtx.putImageData(imageData, 0, 0);
    const ctx = canvas.getContext('2d');
    ctx.filter = 'blur(3px)';
    ctx.drawImage(
      square,
      boxed.x,
      boxed.y,
      boxed.width,
      boxed.height,
      0,
      0,
      canvas.width,
      canvas.height
    );
    ctx.filter = 'none';
    return canvas;
  }

  function cutOutOnWhite(image, maskCanvas) {
    const src = sourceSize(image);
    const cut = document.createElement('canvas');
    cut.width = src.width;
    cut.height = src.height;
    const cutCtx = cut.getContext('2d', { willReadFrequently: true });
    cutCtx.drawImage(image, 0, 0);
    if (maskCanvas) {
      cutCtx.globalCompositeOperation = 'destination-in';
      cutCtx.drawImage(maskCanvas, 0, 0, src.width, src.height);
      cutCtx.globalCompositeOperation = 'source-over';
    }
    const tight = maskCanvas ? trimTransparent(cut, 0.06) : cut;
    const white = document.createElement('canvas');
    white.width = tight.width;
    white.height = tight.height;
    const whiteCtx = white.getContext('2d', { willReadFrequently: true });
    whiteCtx.fillStyle = '#ffffff';
    whiteCtx.fillRect(0, 0, white.width, white.height);
    whiteCtx.drawImage(tight, 0, 0);
    return { cut: tight, white };
  }

  function inkFromLineMap(data, size, box, maskCanvas) {
    let max = -Infinity;
    for (let i = 0; i < data.length; i += 1) {
      if (data[i] > max) max = data[i];
    }
    const scale = max <= 1.5 ? 1 : 1 / 255;
    const square = document.createElement('canvas');
    square.width = size;
    square.height = size;
    const squareCtx = square.getContext('2d');
    const imageData = squareCtx.createImageData(size, size);
    const pixels = imageData.data;
    for (let p = 0, i = 0; p < data.length; p += 1, i += 4) {
      const paper = Math.min(1, Math.max(0, data[p] * scale));
      const ink = 1 - paper;
      const strength = ink < 0.07 ? 0 : Math.min(1, Math.pow((ink - 0.04) / 0.96, 0.72) * 1.35);
      pixels[i] = 28;
      pixels[i + 1] = 24;
      pixels[i + 2] = 22;
      pixels[i + 3] = Math.round(strength * 255);
    }
    squareCtx.putImageData(imageData, 0, 0);

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(2, Math.round(box.width));
    canvas.height = Math.max(2, Math.round(box.height));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(
      square,
      box.x,
      box.y,
      box.width,
      box.height,
      0,
      0,
      canvas.width,
      canvas.height
    );
    if (maskCanvas) {
      ctx.globalCompositeOperation = 'destination-in';
      ctx.filter = 'blur(10px)';
      ctx.drawImage(maskCanvas, 0, 0, canvas.width, canvas.height);
      ctx.filter = 'none';
      ctx.globalCompositeOperation = 'source-over';
    }
    return trimTransparent(canvas);
  }

  function trimTransparent(canvas, paddingRatio) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        if (data[(y * width + x) * 4 + 3] > 12) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX <= minX || maxY <= minY) return canvas;
    const pad = Math.round(Math.max(width, height) * (paddingRatio || 0.04));
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad);
    maxY = Math.min(height - 1, maxY + pad);
    const trimmed = document.createElement('canvas');
    trimmed.width = Math.max(2, maxX - minX + 1);
    trimmed.height = Math.max(2, maxY - minY + 1);
    trimmed.getContext('2d').drawImage(
      canvas,
      minX,
      minY,
      trimmed.width,
      trimmed.height,
      0,
      0,
      trimmed.width,
      trimmed.height
    );
    return trimmed;
  }

  async function toLineArt(image) {
    const engines = await ensureEngines();
    const mask = await subjectMask(image, engines.cutout, engines.ort).catch(() => null);
    const prepared = cutOutOnWhite(image, mask);
    const boxed = letterboxPhoto(prepared.white, LINEART.drawSize, '#ffffff');
    const tensor = canvasToPlanarRgb(boxed.ctx, LINEART.drawSize, false);
    const feeds = {};
    feeds[engines.draw.inputNames[0]] = new engines.ort.Tensor('float32', tensor, [
      1,
      3,
      LINEART.drawSize,
      LINEART.drawSize,
    ]);
    const results = await engines.draw.run(feeds);
    const output = results[engines.draw.outputNames[0]];
    return inkFromLineMap(output.data, LINEART.drawSize, boxed, prepared.cut);
  }

  function loadImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error(COPY.unsupported));
      };
      image.src = url;
    });
  }

  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error(COPY.convertError));
      }, 'image/png');
    });
  }

  function initPersonalizedProduct(root) {
    if (!root || root.dataset.pcInit) return;
    root.dataset.pcInit = 'true';

    const jsonEl = root.querySelector('[data-pc-product]');
    const form = root.querySelector('[data-pc-form]');
    if (!jsonEl || !form) return;

    let productData;
    try {
      productData = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return;
    }

    const currency = jsonEl.dataset.currency;
    const variantInput = form.querySelector('[data-pc-variant-id]');
    const modelProp = form.querySelector('[data-pc-model-prop]');
    const modelSelect = form.querySelector('[data-pc-model-select]');
    const fileInput = form.querySelector('[data-pc-file]');
    const dropzone = form.querySelector('[data-pc-dropzone]');
    const uploadLabel = form.querySelector('[data-pc-upload-label]');
    const live = form.querySelector('[data-pc-live]');
    const submit = form.querySelector('[data-pc-submit]');
    const priceEl = root.querySelector('[data-pc-price]');
    const submitPrice = form.querySelector('[data-pc-submit-price]');
    const mockup = root.querySelector('[data-pc-mockup]');
    const stage = root.querySelector('[data-pc-art-stage]');
    const canvas = root.querySelector('[data-pc-canvas]');
    const placeholder = root.querySelector('[data-pc-placeholder]');
    const hint = root.querySelector('[data-pc-hint]');
    const ctx = canvas.getContext('2d');

    const transform = { scale: 1, x: 0, y: 0 };
    let artCanvas = null;
    let dragging = false;
    let lastPoint = null;

    function setLive(message) {
      if (live) live.textContent = message || '';
    }

    function applyTransform() {
      canvas.style.transform = `translate(-50%, -50%) translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`;
    }

    function paintPreview() {
      if (!artCanvas) return;
      canvas.width = artCanvas.width;
      canvas.height = artCanvas.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(artCanvas, 0, 0);
      canvas.hidden = false;
      if (placeholder) placeholder.hidden = true;
      applyTransform();
    }

    function fitArt() {
      if (!artCanvas || !stage) return;
      const rect = stage.getBoundingClientRect();
      transform.scale = Math.min(rect.width / artCanvas.width, rect.height / artCanvas.height) * 0.72;
      transform.x = 0;
      transform.y = rect.height * 0.05;
      applyTransform();
    }

    function bumperHex() {
      const checked = form.querySelector('[data-pc-color]:checked');
      return colorFromName(checked ? checked.value : '');
    }

    function paintSwatches() {
      root.querySelectorAll('[data-pc-swatch-dot]').forEach((dot) => {
        dot.style.background = colorFromName(dot.getAttribute('data-color'));
      });
    }

    function updateMockupColor() {
      const hex = bumperHex();
      mockup.style.setProperty('--pc-bumper', hex);
      mockup.style.setProperty('--pc-paper', paperFromBumper(hex));
    }

    function getSelectedOptions() {
      return (productData.options || []).map((name, index) => {
        const checked = form.querySelector(`[data-pc-option-input][data-pc-option-index="${index}"]:checked`);
        if (checked) return checked.value;
        const select = form.querySelector(`[data-pc-option-select="${index}"]`);
        if (select) return select.value;
        return productData.variants[0] ? productData.variants[0].options[index] : '';
      });
    }

    function getSelectedVariant() {
      const options = getSelectedOptions();
      return productData.variants.find((variant) =>
        variant.options.every((option, index) => option === options[index])
      ) || productData.variants[0];
    }

    function selectedModel() {
      if (modelSelect && modelSelect.value) return modelSelect.value;
      const variant = getSelectedVariant();
      if (variant && variant.title && variant.title !== 'Default Title') {
        return variant.options[0];
      }
      return '';
    }

    function updateVariantUI() {
      const variant = getSelectedVariant();
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      if (modelProp) modelProp.value = selectedModel();

      form.querySelectorAll('.pc-swatch, .pc-pill').forEach((el) => {
        const input = el.querySelector('input');
        el.classList.toggle('is-active', Boolean(input && input.checked));
      });

      if (priceEl) priceEl.textContent = formatMoney(variant.price, currency);
      if (submitPrice) submitPrice.textContent = formatMoney(variant.price, currency);
      if (submit) {
        submit.disabled = !variant.available;
        if (!variant.available) submit.textContent = 'SOLD OUT';
      }
      updateMockupColor();
    }

    async function handleFile(file) {
      if (!file || (file.type && !file.type.startsWith('image/'))) {
        setLive(COPY.unsupported);
        return;
      }
      setLive(COPY.loadingModel);
      dropzone.classList.remove('is-ready');
      if (stage) stage.classList.add('is-busy');
      if (placeholder) {
        placeholder.hidden = false;
        placeholder.textContent = COPY.loadingModel;
      }
      canvas.hidden = true;
      try {
        const image = await loadImage(file);
        setLive(COPY.converting);
        if (placeholder) placeholder.textContent = COPY.converting;
        artCanvas = await toLineArt(image);
        transform.scale = 1;
        transform.x = 0;
        transform.y = 0;
        paintPreview();
        fitArt();
        if (uploadLabel) uploadLabel.textContent = file.name;
        dropzone.classList.add('is-ready');
        setLive(COPY.ready);
        if (hint) hint.textContent = COPY.ready;
      } catch (error) {
        artCanvas = null;
        canvas.hidden = true;
        if (placeholder) {
          placeholder.hidden = false;
          placeholder.textContent = 'Upload a photo to see it as line art on this case.';
        }
        setLive(error.message || COPY.convertError);
      } finally {
        if (stage) stage.classList.remove('is-busy');
      }
    }

    async function exportPrintFile() {
      if (!artCanvas || !stage) return null;
      const rect = stage.getBoundingClientRect();
      const print = document.createElement('canvas');
      print.width = 1200;
      print.height = Math.round(1200 * (rect.height / rect.width));
      const printCtx = print.getContext('2d');
      printCtx.fillStyle = paperFromBumper(bumperHex());
      printCtx.fillRect(0, 0, print.width, print.height);

      const factor = print.width / rect.width;
      const drawWidth = artCanvas.width * transform.scale * factor;
      const drawHeight = artCanvas.height * transform.scale * factor;
      const dx = print.width / 2 + transform.x * factor - drawWidth / 2;
      const dy = print.height / 2 + transform.y * factor - drawHeight / 2;
      printCtx.drawImage(artCanvas, dx, dy, drawWidth, drawHeight);
      return canvasToBlob(print);
    }

    function addToCart(formData) {
      const rootUrl = window.Shopify?.routes?.root || '/';
      return fetch(`${rootUrl}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().then((data) => {
              throw new Error(data.description || COPY.addError);
            });
          }
          return response.json();
        })
        .then(() => fetch(`${rootUrl}cart.js`))
        .then((response) => response.json());
    }

    form.querySelectorAll('[data-pc-option-input], [data-pc-color]').forEach((input) => {
      input.addEventListener('change', updateVariantUI);
    });
    form.querySelectorAll('[data-pc-option-select], [data-pc-model-select]').forEach((select) => {
      select.addEventListener('change', updateVariantUI);
    });

    fileInput.addEventListener('change', () => {
      const file = fileInput.files && fileInput.files[0];
      if (file) handleFile(file);
    });

    ;['dragenter', 'dragover'].forEach((eventName) => {
      dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        dropzone.classList.add('is-ready');
      });
    });
    ;['dragleave', 'drop'].forEach((eventName) => {
      dropzone.addEventListener(eventName, (event) => {
        event.preventDefault();
        if (eventName === 'drop') {
          const file = event.dataTransfer.files && event.dataTransfer.files[0];
          if (file) {
            try {
              const transfer = new DataTransfer();
              transfer.items.add(file);
              fileInput.files = transfer.files;
            } catch (error) {}
            handleFile(file);
          }
        }
      });
    });

    stage.addEventListener('pointerdown', (event) => {
      if (!artCanvas) return;
      dragging = true;
      lastPoint = { x: event.clientX, y: event.clientY };
      stage.classList.add('is-dragging');
      stage.setPointerCapture(event.pointerId);
    });
    stage.addEventListener('pointermove', (event) => {
      if (!dragging || !lastPoint) return;
      transform.x += event.clientX - lastPoint.x;
      transform.y += event.clientY - lastPoint.y;
      lastPoint = { x: event.clientX, y: event.clientY };
      applyTransform();
    });
    const stopDrag = () => {
      dragging = false;
      lastPoint = null;
      stage.classList.remove('is-dragging');
    };
    stage.addEventListener('pointerup', stopDrag);
    stage.addEventListener('pointercancel', stopDrag);
    stage.addEventListener(
      'wheel',
      (event) => {
        if (!artCanvas) return;
        event.preventDefault();
        const next = transform.scale * (event.deltaY < 0 ? 1.06 : 0.94);
        transform.scale = Math.min(4, Math.max(0.35, next));
        applyTransform();
      },
      { passive: false }
    );

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const variant = getSelectedVariant();
      if (!variant || !variant.available) return;
      if (!artCanvas || !fileInput.files || !fileInput.files[0]) {
        setLive(COPY.needPhoto);
        return;
      }
      const model = selectedModel();
      if (!model) {
        setLive(COPY.needModel);
        return;
      }

      const original = submit.innerHTML;
      submit.disabled = true;
      submit.textContent = COPY.adding;

      try {
        if (modelProp) modelProp.value = model;
        const formData = new FormData(form);
        formData.set('id', String(variant.id));
        formData.set('quantity', '1');
        formData.set('properties[Phone model]', model);
        formData.set('properties[Art style]', 'Line drawing');
        const printBlob = await exportPrintFile();
        if (printBlob) {
          const artFile = new File([printBlob], 'line-art.png', { type: 'image/png' });
          formData.append('properties[Line art]', artFile);
        }
        const cart = await addToCart(formData);
        document.querySelectorAll('[data-cart-count]').forEach((badge) => {
          badge.textContent = cart.item_count;
        });
        document.dispatchEvent(new CustomEvent('peeperly:cart:updated', { detail: cart }));
        document.dispatchEvent(new CustomEvent('peeperly:cart:open'));
        submit.textContent = COPY.added;
        window.setTimeout(() => {
          submit.disabled = false;
          submit.innerHTML = original;
        }, 1200);
      } catch (error) {
        submit.disabled = false;
        submit.innerHTML = original;
        setLive(error.message || COPY.addError);
      }
    });

    paintSwatches();
    updateVariantUI();
  }

  function boot() {
    ensureEngines().catch(() => {});
    document.querySelectorAll('[data-pc-root]').forEach(initPersonalizedProduct);
  }

  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
